import { type ReactNode, useCallback } from "react";

import { Drawer, type DrawerProps } from "antd";
import { useTracking } from "react-tracking";

/**
 * core-components typechecks with `exactOptionalPropertyTypes`, under which an optional
 * prop does not accept an explicit `undefined`. Callers forwarding a possibly-absent value
 * (`onClose={props.onClose}`) are the normal case for a drawer, so each picked prop admits
 * `undefined` alongside its own type.
 */
type WithExplicitUndefined<T> = { [K in keyof T]: T[K] | undefined };

export interface IBTDrawerProps
  extends WithExplicitUndefined<
    Pick<
      DrawerProps,
      | "className"
      | "placement"
      | "visible"
      | "afterVisibleChange"
      | "forceRender"
      | "width"
      | "height"
      | "closable"
      | "onClose"
      | "mask"
      | "title"
      | "footer"
      | "destroyOnClose"
      | "keyboard"
      | "bodyStyle"
      | "push"
      | "getContainer"
      | "style"
      | "zIndex"
      | "contentWrapperStyle"
      | "drawerStyle"
    >
  > {
  children: ReactNode;
  role?: React.AriaRole;
  /** Accessible name for the drawer, needed when it has no visible title of its own. */
  "aria-label"?: string;
}

export function BTDrawer({
  children,
  visible,
  afterVisibleChange,
  ...otherProps
}: IBTDrawerProps) {
  const { trackEvent, Track } = useTracking<Record<string, unknown>>({
    element: "Drawer",
  });

  const handleVisibleChange = useCallback(() => {
    if (visible) {
      trackEvent({
        event: "DrawerOpen",
      });
    } else {
      trackEvent({
        event: "DrawerClose",
      });
    }
    afterVisibleChange?.(visible ? visible : false);
  }, [visible, trackEvent, afterVisibleChange]);

  return (
    <Track>
      <Drawer
        afterVisibleChange={handleVisibleChange}
        visible={visible ?? false}
        // The cast only re-narrows what WithExplicitUndefined widened: every key here comes
        // from DrawerProps, and antd reads an explicitly-undefined prop the same as an absent
        // one. It is not covering a shape mismatch.
        {...(otherProps as DrawerProps)}
      >
        {children}
      </Drawer>
    </Track>
  );
}
